import {
  Button,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { UnsavedChangesModal } from '@/shared/ui/UnsavedChangesModal';
import { FORM_TABS, FORM_TAB_ARRAY } from '../EditPersonaForm/EditPersonaForm.schema';
import { EditPersonaFormProvider } from '../EditPersonaForm/context/EditPersonaFormProvider';
import { useEditPersonaForm } from '../EditPersonaForm/hooks/useEditPersonaForm';
import { PersonaInformationTabV2, ProfileMediaTabV2 } from '../EditPersonaForm/tabsV2';
import { RequiredFieldsLegendV2 } from '../PersonaFormSectionsV2';
import { SpaceVisibilitySection } from '../PersonaFormSections';
import type { EditPersonaSheetProps } from './EditPersonaSheet.types';

/**
 * The edit-audience form, on a right-side sheet. Validation, dirty tracking and
 * the unsaved-changes guard live in `useEditPersonaForm`, not here.
 */
export function EditPersonaSheet({
  persona,
  updatePersona,
  onClose,
  onSuccess,
}: EditPersonaSheetProps) {
  const { t } = useTranslation('audiences');

  const form = useEditPersonaForm({
    persona,
    updatePersona,
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
    onClose,
  });

  const isLocked = form.isSubmitting || form.isProcessingMedia;
  // A Default persona is the platform's own; the API refuses edits to it.
  const isDefaultPersona = persona.type === 'Default';

  return (
    <>
      <Sheet
        // The unsaved-changes prompt takes over rather than stacking on the sheet.
        open={!form.showUnsavedModal}
        onOpenChange={(next) => !next && !isLocked && form.handleClose()}
      >
        {/* The shared shells' layout, kept here because neither shell holds tabs in its header:
            the panel drops its horizontal padding so the body scrolls edge to edge, with its
            inset (and an edge control's focus ring) inside its own clip. */}
        <SheetContent className="flex w-[720px] flex-col gap-sm rounded-l-lg px-0 py-xl sm:max-w-none">
          <SheetHeader className="gap-md p-0 px-xl">
            <SheetTitle>{t('audiences.editPersonaForm.editPersona')}</SheetTitle>
            <Tabs
              value={String(form.activeTabValue)}
              onValueChange={(next) => form.handleTabChange(Number(next))}
            >
              <TabsList>
                {FORM_TAB_ARRAY.map((tab) => (
                  <TabsTrigger key={tab.id} value={String(tab.value)} disabled={isLocked}>
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </SheetHeader>

          <EditPersonaFormProvider form={form}>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-xl py-2xs">
              {form.activeTab.id === FORM_TABS.personaInformation.id && <PersonaInformationTabV2 />}
              {form.activeTab.id === FORM_TABS.profileMedia.id && <ProfileMediaTabV2 />}
              {form.activeTab.id === FORM_TABS.spaceVisibility.id && (
                <SpaceVisibilitySection
                  disabled={!form.canEditSpaceVisibility}
                  includeCurrentWorkspace={form.includeCurrentWorkspaceOption}
                />
              )}
            </div>
          </EditPersonaFormProvider>

          <SheetFooter className="flex-row items-center justify-between gap-sm p-0 px-xl">
            <RequiredFieldsLegendV2 />
            <div className="flex items-center gap-xs">
              <Button variant="outline" size="lg" disabled={isLocked} onClick={form.handleClose}>
                {t('audiences.editPersonaForm.cancel')}
              </Button>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        size="lg"
                        disabled={isDefaultPersona || !form.isDirty || isLocked}
                        onClick={() => void form.submitForm()}
                      >
                        {t('audiences.editPersonaForm.saveChanges')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isDefaultPersona && (
                    <TooltipContent>
                      {t('audiences.editPersonaForm.defaultPersonasCannotBeEdited')}
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <UnsavedChangesModal
        open={form.showUnsavedModal}
        onCancel={form.handleCancelLeave}
        onConfirmLeave={form.handleConfirmLeave}
      />
    </>
  );
}

EditPersonaSheet.displayName = 'EditPersonaSheet';
